import { attr, ProtoBaseModel } from '@square/dashboard-data/utils/ember-model-stubs';
import SignatureSettings from './signature-settings';
import OfflineModeSettings from './offline-mode-settings';
import TippingSettings from './tipping-settings';
import SeatingSettings from './seating-settings';
import OrderTicketsSettings from './order-tickets-settings';
import CustomerManagementSettings from './customer-management-settings';
import PaymentSoundsSettings from './payment-sounds-settings';
import PaymentTypesSettings from './payment-types-settings';
import QuickAmountSettings from './quick-amount-settings';
import SecuritySettings from './security-settings';
import TileSettings from './tile-settings';
import { FirstPartyAddon } from '@square/dashboard-protos/models/squareup/addons/shared/first_party_addons';
import BaseAddonsSettings from './addons/base-addons-settings';
import CashManagementSettings from './addons/cash-management-settings';
import LoyaltySettings from './addons/loyalty-settings';
import OpenTicketsSettings from './addons/open-tickets-settings';
import TeamManagementSettings from './addons/team-management-settings';
import StaffSettings from './addons/staff-settings';
import TextMessageMarketingSettings from './addons/text-message-marketing-settings';
import RstReleaseManagerSettings from './release-manager/rst-release-manager-features-settings';
import OrdersModeSettings from './orders-mode-settings';
import AppearanceSettings from './appearance-settings';
import CourseManagementSettings from './course-management-settings';
import FloorPlanSettings from './floor-plan-settings';
import OrderHubSettings from './order-hub-settings';
import CheckoutSettings from './checkout-settings';
import KdsFetchSettings from './kds-fetch-settings';
import KdsUiSettings from './kds-ui-settings';
import FnbKioskSettings from './fnb-kiosk-settings';
import TextCustomerSettings from './text-customer-settings';
import ScanToPaySettings from './scan-to-pay-settings';
import StandaloneCheckoutSettings from './standalone-checkout-settings';
import TerminalApiSecuritySettings from './tapi-security-settings';
import CustomerDisplaySettings from './customer-display-settings';
import BuyerFacingDisplaySettings from './buyer-facing-display-settings';
import ItemDetailsSettings from './item-details-settings';
import FulfillmentSettings from './fulfillment-settings';
import NextGenSettings from './release-manager/next-gen-settings';
import ModifierSequencingSettings from './release-manager/modifier-sequencing-settings';
import NegativeStockWarningSettings from './negative-stock-warning-settings';
import CheckoutConvergenceItemDetailsSettings from './release-manager/checkout-convergence-item-details-settings';
import CheckoutConvergenceItemGridSettings from './release-manager/checkout-convergence-item-grid-settings';
export function parseAddonSettings(addonsSettings) {
  const settings = {};
  for (const settingProperty in addonsSettings) {
    const addonId = addonsSettings[settingProperty].first_party_addon;
    let settingModel;
    switch (FirstPartyAddon[addonId]) {
      // DPV2 INTEGRATION POINT: add new addon settings here
      case FirstPartyAddon.CASH_MANAGEMENT:
        settingModel = CashManagementSettings.deserialize(addonsSettings[settingProperty]);
        break;
      case FirstPartyAddon.LOYALTY:
        settingModel = LoyaltySettings.deserialize(addonsSettings[settingProperty]);
        break;
      case FirstPartyAddon.OPEN_TICKETS:
        settingModel = OpenTicketsSettings.deserialize(addonsSettings[settingProperty]);
        break;
      case FirstPartyAddon.TEAM_MANAGEMENT:
        settingModel = TeamManagementSettings.deserialize(addonsSettings[settingProperty]);
        break;
      case FirstPartyAddon.STAFF:
        settingModel = StaffSettings.deserialize(addonsSettings[settingProperty]);
        break;
      case FirstPartyAddon.TEXT_MESSAGE_MARKETING:
        settingModel = TextMessageMarketingSettings.deserialize(addonsSettings[settingProperty]);
        break;
      default:
        settingModel = BaseAddonsSettings.deserialize(addonsSettings[settingProperty]);
        break;
    }
    settings[addonId] = {
      protoPath: settingProperty,
      setting: settingModel
    };
  }
  return settings;
}
export default ProtoBaseModel.extend({
  // DPV2 INTEGRATION POINT: add new settings here
  signature: attr(SignatureSettings),
  tipping: attr(TippingSettings),
  offlineMode: attr(OfflineModeSettings),
  orderTickets: attr(OrderTicketsSettings),
  customerManagement: attr(CustomerManagementSettings),
  paymentSounds: attr(PaymentSoundsSettings),
  paymentTypes: attr(PaymentTypesSettings),
  quickAmount: attr(QuickAmountSettings),
  security: attr(SecuritySettings),
  tile: attr(TileSettings),
  rstReleaseManagerFeatures: attr(RstReleaseManagerSettings),
  seating: attr(SeatingSettings),
  ordersMode: attr(OrdersModeSettings),
  appearance: attr(AppearanceSettings),
  courseManagement: attr(CourseManagementSettings),
  floorPlan: attr(FloorPlanSettings),
  orderHub: attr(OrderHubSettings),
  checkout: attr(CheckoutSettings),
  kdsFetch: attr(KdsFetchSettings),
  kdsUi: attr(KdsUiSettings),
  fnbKiosk: attr(FnbKioskSettings),
  textCustomer: attr(TextCustomerSettings),
  scanToPay: attr(ScanToPaySettings),
  standaloneCheckout: attr(StandaloneCheckoutSettings),
  tapiSecurity: attr(TerminalApiSecuritySettings),
  customerDisplay: attr(CustomerDisplaySettings),
  buyerFacingDisplay: attr(BuyerFacingDisplaySettings),
  itemDetails: attr(ItemDetailsSettings),
  fulfillment: attr(FulfillmentSettings),
  nextGenSettings: attr(NextGenSettings),
  modifierSequencingSettings: attr(ModifierSequencingSettings),
  negativeStockWarning: attr(NegativeStockWarningSettings),
  checkoutConvergenceItemDetailsSettings: attr(CheckoutConvergenceItemDetailsSettings),
  checkoutConvergenceItemGridSettings: attr(CheckoutConvergenceItemGridSettings),
  // This will hold addon proto field addonId --> {protoPath, setting class}
  addonsSettings: attr(Object, {
    defaultValue: {}
  }),
  convertToJson() {
    return {
      // DPV2 INTEGRATION POINT: add new settings here
      tipping: this.tipping?.toJSON(),
      signature: this.signature?.toJSON(),
      store_and_forward: this.offlineMode?.toJSON(),
      order_tickets: this.orderTickets?.toJSON(),
      customer_management: this.customerManagement?.toJSON(),
      payment_sounds: this.paymentSounds?.toJSON(),
      payment_types: this.paymentTypes?.toJSON(),
      quick_amount: this.quickAmount?.toJSON(),
      quick_amounts: this.quickAmount?.toJSON(),
      security: this.security?.toJSON(),
      tile: this.tile?.toJSON(),
      seating: this.seating?.toJSON(),
      orders_mode: this.ordersMode?.toJSON(),
      appearance: this.appearance?.toJSON(),
      course_management: this.courseManagement?.toJSON(),
      floor_plan: this.floorPlan?.toJSON(),
      order_hub: this.orderHub?.toJSON(),
      checkout: this.checkout?.toJSON(),
      kds_fetch: this.kdsFetch?.toJSON(),
      kds_ui: this.kdsUi?.toJSON(),
      addons_settings: this.convertAddonsToJson(),
      rst_release_manager_features: this.rstReleaseManagerFeatures?.toJSON(),
      fnb_kiosk: this.fnbKiosk?.toJSON(),
      text_customer: this.textCustomer?.toJSON(),
      scan_to_pay: this.scanToPay?.toJSON(),
      standalone_settings: this.standaloneCheckout?.toJSON(),
      security_settings: this.tapiSecurity?.toJSON(),
      idle_screen_settings: this.customerDisplay?.toJSON(),
      buyer_facing_display: this.buyerFacingDisplay?.toJSON(),
      item_details: this.itemDetails?.toJSON(),
      fulfillment_types: this.fulfillment?.toJSON(),
      next_gen_settings: this.nextGenSettings?.toJSON(),
      modifier_sequencing_settings: this.modifierSequencingSettings?.toJSON(),
      negative_stock_warning: this.negativeStockWarning?.toJSON(),
      checkout_convergence_item_details_settings: this.checkoutConvergenceItemDetailsSettings?.toJSON(),
      checkout_convergence_item_grid_settings: this.checkoutConvergenceItemGridSettings?.toJSON()
    };
  },
  convertAddonsToJson() {
    const settings = {};
    for (const value of Object.values(this.addonsSettings)) {
      settings[value.protoPath] = value.setting.toJSON();
    }
    return settings;
  }
}).reopenClass({
  toDeviceProfileSettingsV2(settings = {}) {
    // DPV2 INTEGRATION POINT: add new settings here
    const data = {
      tipping: TippingSettings.deserialize(settings.tipping),
      signature: SignatureSettings.deserialize(settings.signature),
      offlineMode: OfflineModeSettings.deserialize(settings.store_and_forward),
      orderTickets: OrderTicketsSettings.deserialize(settings.order_tickets),
      customerManagement: CustomerManagementSettings.deserialize(settings.customer_management),
      paymentSounds: PaymentSoundsSettings.deserialize(settings.payment_sounds),
      paymentTypes: PaymentTypesSettings.deserialize(settings.payment_types),
      quickAmount: QuickAmountSettings.deserialize(
      // This is because RST & SPOS has different field name for both
      // it's hard to rename the protos right now.
      settings.quick_amount ?? settings.quick_amounts),
      security: SecuritySettings.deserialize(settings.security),
      tile: TileSettings.deserialize(settings.tile),
      seating: SeatingSettings.deserialize(settings.seating),
      ordersMode: OrdersModeSettings.deserialize(settings.orders_mode),
      appearance: AppearanceSettings.deserialize(settings.appearance),
      courseManagement: CourseManagementSettings.deserialize(settings.course_management),
      floorPlan: FloorPlanSettings.deserialize(settings.floor_plan),
      orderHub: OrderHubSettings.deserialize(settings.order_hub),
      checkout: CheckoutSettings.deserialize(settings.checkout),
      kdsFetch: KdsFetchSettings.deserialize(settings.kds_fetch),
      kdsUi: KdsUiSettings.deserialize(settings.kds_ui),
      addonsSettings: parseAddonSettings(settings.addons_settings ?? {}),
      rstReleaseManagerFeatures: RstReleaseManagerSettings.deserialize(settings.rst_release_manager_features ?? {}),
      fnbKiosk: FnbKioskSettings.deserialize(settings.fnb_kiosk),
      textCustomer: TextCustomerSettings.deserialize(settings.text_customer),
      scanToPay: ScanToPaySettings.deserialize(settings.scan_to_pay),
      standaloneCheckout: StandaloneCheckoutSettings.deserialize(settings.standalone_settings),
      tapiSecurity: TerminalApiSecuritySettings.deserialize(settings.security_settings),
      customerDisplay: CustomerDisplaySettings.deserialize(settings.idle_screen_settings),
      buyerFacingDisplay: BuyerFacingDisplaySettings.deserialize(settings.buyer_facing_display),
      itemDetails: ItemDetailsSettings.deserialize(settings.item_details),
      fulfillment: FulfillmentSettings.deserialize(settings.fulfillment_types),
      nextGenSettings: NextGenSettings.deserialize(settings.next_gen_settings),
      modifierSequencingSettings: ModifierSequencingSettings.deserialize(settings.modifier_sequencing_settings),
      negativeStockWarning: NegativeStockWarningSettings.deserialize(settings.negative_stock_warning),
      checkoutConvergenceItemDetailsSettings: CheckoutConvergenceItemDetailsSettings.deserialize(settings.checkout_convergence_item_details_settings),
      checkoutConvergenceItemGridSettings: CheckoutConvergenceItemGridSettings.deserialize(settings.checkout_convergence_item_grid_settings)
    };
    return this.create(data);
  }
});